import { TemplateActivities } from "../../../models/TemplateActivities";
import { useEffect } from "react";
import {
  AddEmissionContext,
  EmissionMode,
  FormValuesStepTwo,
} from "../../../contexts/AddEmissionContext";
import CustomModal from "../../../components/modal/CustomModal";
import { Typography, Box, Button } from "@mui/material";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { DROPDOWN_EMISSION_GROUP } from "../../../components/emission/emission-components";
import dayjs, { Dayjs } from "dayjs";
import alertService from "../../../components/alert/alertService";
import { Trans } from "react-i18next";
import TemplateActivitiesService from "../../../service/template-activities.service";
import { ActivityTemplatesContext } from "../../../contexts/ActivityTemplatesContext";
import { useAuth } from "../../../contexts/UserContext";
import { OrganizationBreadcrumbs } from "../../emission/_components/OrganizationBreadcrumbs";
import AddEmissionStepTwo from "../../emission/_components/popup-add-emission/step2/AddEmissionStepTwo";

interface Props {
  row: TemplateActivities;
  open: boolean;
  onClose: () => void;
}

export const EditActivityTemplates = (props: Props) => {
  const { organization } = useAuth();

  const {
    stepOneForm,
    stepTwoForm,
    subOrganizationList,
    assetList,
    setEmissionMode,
    setDisableMonthly,
    prepareFormDataToCreateOrDraftTemplates,
    refreshEmissionFactors,
  } = AddEmissionContext();

  const { transformersToDataGrid } = ActivityTemplatesContext();

  const onSubmit = async (_data: FormValuesStepTwo) => {
    const isConfirmed = await alertService.confirm(
      t("Edit"),
      <Box>
        <Trans
          t={t}
          i18nKey="edit-templates"
          values={{ id: props.row.id }}
          components={{
            span: <span style={{ color: "var(--red)" }} />,
          }}
        />

        <Typography variant="text2" display="block" mt={1} color="var(--red)">
          <Trans t={t} i18nKey="edit-templates-emission-warning" />
        </Typography>
      </Box>,
      t("Save"),
    );

    if (isConfirmed) {
      const formData = prepareFormDataToCreateOrDraftTemplates(
        props.row.lastGeneratedAt === null ? false : true,
      );

      let result;
      try {
        alertService.showLoading();
        result = await TemplateActivitiesService.editTemplateActivities(
          formData,
          props.row._id,
        );
      } catch {
        // error alert already shown by service
        return;
      } finally {
        alertService.closeLoading();
      }

      if (result) {
        transformersToDataGrid(result.templateActivities);
        alertService.mixin(t("Successfully"));
        close();
      }
    }
  };

  const close = () => {
    props.onClose();
    setTimeout(() => {
      stepTwoForm.reset();
      stepOneForm.reset();
    }, 300);
  };

  const setUpForm = (templateYear: Dayjs) => {
    stepTwoForm.setValue("activityGroup", props.row.activityGroupEN ?? "");
    stepTwoForm.setValue("activityType", props.row.activityTypeEn);
    stepTwoForm.setValue("activityAttribute", props.row.activityAttributeEN);
    // the stored factor only fits the stored year; a move during load keeps the new year's default
    if (dayjs(stepTwoForm.getValues("year")).isSame(templateYear, "year")) {
      stepTwoForm.setValue(
        "activityEmissionFactorPublisher",
        props.row.emissionFactorRef ?? "",
      );
    }
    stepTwoForm.setValue("amount", props.row.amount.toString());
    stepTwoForm.setValue("unit", props.row.unit ?? "");
    stepTwoForm.setValue("remark", props.row.remark ?? "");

    if (props.row.auxiliary) {
      stepTwoForm.setValue("aux1", props.row.auxiliary.aux1 ?? "");
      stepTwoForm.setValue("aux2", props.row.auxiliary.aux2 ?? "");
      stepTwoForm.setValue("aux3", props.row.auxiliary.aux3 ?? "");
      stepTwoForm.setValue("aux4", props.row.auxiliary.aux4 ?? "");
      stepTwoForm.setValue("aux5", props.row.auxiliary.aux5 ?? "");
    }

    stepTwoForm.setValue("monthly", props.row.isMonthly ?? false);
    stepTwoForm.setValue("vendor", props.row.vendor ?? "");
  };

  useEffect(() => {
    if (!props.open) {
      setDisableMonthly(false);
      return;
    }

    setEmissionMode(EmissionMode.AddActivityTemplates);
    setDisableMonthly(!!props.row.lastGeneratedAt);

    const initData = async () => {
      const item = subOrganizationList.find((i) => {
        return i.name.trim() === props.row.suborganizationName.trim();
      });

      stepOneForm.setValue("subOrganizations", item?.id as string);
      stepOneForm.setValue(
        "location",
        `${item?.location?.address ?? ""} ${item?.location?.district ?? ""} ${
          item?.location?.city ?? ""
        }`.trim(),
      );

      const emissionGroupRef: string | undefined = DROPDOWN_EMISSION_GROUP.find(
        (i) => i.emissionGroup === props.row.emissionGroup,
      )?.emissionGroup;

      stepOneForm.setValue("emissionGroup", emissionGroupRef as string);
      stepOneForm.setValue(
        "assetName",
        assetList.find(
          (i) =>
            i.name === props.row.assetName &&
            i.suborganizationName === props.row.suborganizationName &&
            i.siteName === props.row.siteName,
        )?._id ?? "",
      );

      const templateYear = dayjs(`${props.row.year}-06-01`);
      stepTwoForm.setValue("year", templateYear);
      stepTwoForm.setValue("startDate", templateYear.startOf("year"));
      stepTwoForm.setValue("endDate", templateYear.endOf("year"));

      if (emissionGroupRef) {
        await refreshEmissionFactors();
      }

      setTimeout(() => {
        setUpForm(templateYear);
      }, 50);
    };

    initData();
  }, [props.open]);

  const OnError = (errors: any) => {
    const inputError: string[] = Object.keys(errors);
    const form: HTMLElement | null = document.getElementById(inputError[0]);

    if (form)
      form.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
  };

  return (
    <CustomModal
      header={
        <Box>
          <Typography variant="h1">
            {t("edit-templates_hl")}
            {` AT-${props.row.id}`}{" "}
          </Typography>
          <OrganizationBreadcrumbs
            organization={organization?.name ?? ""}
            location={stepOneForm.getValues("location") ?? ""}
            subOrganizations={
              subOrganizationList.find(
                (i) => i.id === stepOneForm.getValues("subOrganizations"),
              )?.name ?? ""
            }
            site={subOrganizationList
              .find((i) => i.id === stepOneForm.getValues("subOrganizations"))
              ?.sites.find((site) => site.name === props.row.siteName)}
          />
        </Box>
      }
      body={
        <Box height={"80dvh"}>
          <FormProvider {...stepTwoForm}>
            <form
              onSubmit={stepTwoForm.handleSubmit(onSubmit, OnError)}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <AddEmissionStepTwo />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  marginTop: "44px",
                }}
                rowGap={2.5}
              >
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  type="submit"
                  name="add"
                >
                  {t("Save")}
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Box>
      }
      open={props.open}
      onClose={close}
    />
  );
};
